"use client";

import { useCallback, useRef, useState, useSyncExternalStore } from "react";
import { Star, ChevronDown } from "lucide-react";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";

import { useI18n } from "@/components/i18n-provider";
import { useMarketingUser } from "@/components/marketing-user-provider";
import {
  OAUTH_NEXT_COOKIE_MAX_AGE_SECONDS,
  OAUTH_NEXT_COOKIE_NAME,
} from "@/lib/auth/oauth-constants";
import { trackEvent } from "@/lib/analytics";
import { deleteStoredCV, storeCV, type StoredCV } from "@/lib/indexed-db";
import { submitCvForReview } from "@/lib/cv/submit-cv-review";
import { getPendingCvReviewPath } from "@/lib/cv/review-paths";

const CVUploadForm = dynamic(() =>
  import("@/components/cv-form/cv-upload-form").then(
    (module) => module.CVUploadForm,
  ),
);
const CVUploadFormMobile = dynamic(() =>
  import("@/components/cv-form/cv-upload-form-mobile").then(
    (module) => module.CVUploadFormMobile,
  ),
);

type UploadFormSource = "desktop" | "mobile";

const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)";

function subscribeToDesktopMediaQuery(onChange: () => void) {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  if (typeof window.matchMedia !== "function") {
    return () => undefined;
  }

  const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
  mediaQuery.addEventListener("change", onChange);
  return () => mediaQuery.removeEventListener("change", onChange);
}

function getDesktopMediaQuerySnapshot(): boolean | null {
  return typeof window !== "undefined" &&
    typeof window.matchMedia === "function"
    ? window.matchMedia(DESKTOP_MEDIA_QUERY).matches
    : null;
}

function getDesktopMediaQueryServerSnapshot(): boolean | null {
  return null;
}

export function HeroSection() {
  const router = useRouter();
  const { locale, dictionary } = useI18n();
  const user = useMarketingUser();
  const isDesktop = useSyncExternalStore(
    subscribeToDesktopMediaQuery,
    getDesktopMediaQuerySnapshot,
    getDesktopMediaQueryServerSnapshot,
  );
  const isAuthenticated = Boolean(user);
  const [uploadingForm, setUploadingForm] = useState<UploadFormSource | null>(
    null,
  );
  // The disabled CTA is a render away; this ref rejects a second submit that
  // lands before that render commits, so one upload never becomes two CVs.
  const isSubmittingRef = useRef(false);
  const hero = dictionary.landing.heroSection;
  const {
    uploadFailed,
    cvResponseMissing,
    reviewRequestFailed,
    reviewRequestMissing,
    authFailed,
  } = dictionary.cvUploadForm.errors;
  const title = hero.title;
  const titleMatch = title.match(/^(.*)\s*[—–-]\s*([^—–-]+)$/);
  const titleLead = titleMatch ? titleMatch[1].trimEnd() : title;
  const titleHighlight = titleMatch ? titleMatch[2].trim() : "";

  const handleUpload = useCallback(
    async (payload: StoredCV, source: UploadFormSource) => {
      if (isSubmittingRef.current) {
        return;
      }

      isSubmittingRef.current = true;
      setUploadingForm(source);

      try {
        if (!isAuthenticated) {
          const nextPath = getPendingCvReviewPath(locale);
          await storeCV(payload);
          document.cookie = `${OAUTH_NEXT_COOKIE_NAME}=${encodeURIComponent(nextPath)}; path=/; max-age=${OAUTH_NEXT_COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
          trackEvent("oauth_start", { source: "hero", locale });

          try {
            const { createClient } = await import("@/lib/supabase/client");
            const supabase = createClient();
            const { error } = await supabase.auth.signInWithOAuth({
              provider: "google",
              options: {
                redirectTo: `${window.location.origin}/auth/oauth?next=${encodeURIComponent(nextPath)}`,
              },
            });

            if (error) {
              throw error;
            }
          } catch {
            try {
              await deleteStoredCV();
            } catch (cleanupError: unknown) {
              console.error(
                "Failed to clear CV draft after OAuth error",
                cleanupError,
              );
            }

            throw new Error(authFailed);
          }

          return;
        }

        const { reviewRequestId } = await submitCvForReview(payload, {
          uploadFailed,
          cvResponseMissing,
          reviewRequestFailed,
          reviewRequestMissing,
        });

        trackEvent("review_started", {
          source: "hero",
          level: payload.experienceLevel,
        });
        router.push(`/${locale}/dashboard/review/${reviewRequestId}`);
      } catch (error: unknown) {
        isSubmittingRef.current = false;
        setUploadingForm(null);
        throw error;
      }
    },
    [
      authFailed,
      cvResponseMissing,
      isAuthenticated,
      locale,
      reviewRequestFailed,
      reviewRequestMissing,
      router,
      uploadFailed,
    ],
  );

  const handleMobileUpload = useCallback(
    (payload: StoredCV) => handleUpload(payload, "mobile"),
    [handleUpload],
  );
  const handleDesktopUpload = useCallback(
    (payload: StoredCV) => handleUpload(payload, "desktop"),
    [handleUpload],
  );

  return (
    <section
      id="hero-section"
      className="bg-white dark:bg-background font-sans text-text-heading"
    >
      <div className="mx-auto flex max-w-[1440px] flex-col items-center justify-between gap-6 px-4 pb-10 pt-6 sm:px-6 lg:flex-row lg:items-start lg:gap-16 lg:pb-14 lg:pt-8 lg:pl-20 lg:pr-28">
        <div className="flex w-full flex-col items-center gap-6 text-center lg:items-start lg:gap-24 lg:text-left lg:max-w-[670px] lg:px-8 lg:py-6">
          <div className="flex flex-col items-center gap-2 lg:items-start lg:gap-5">
            <span className="hidden w-fit max-w-full items-center justify-start self-start whitespace-nowrap rounded-full border-[2px] border-surface-primary bg-white dark:bg-card px-3 py-1 text-left text-sm font-normal leading-5 text-text-body lg:inline-flex">
              {hero.badge}
            </span>
            <h1 className="max-w-[320px] text-[24px] font-semibold leading-[32px] tracking-[-0.2px] text-text-heading sm:max-w-[420px] sm:text-4xl sm:leading-[44px] lg:max-w-none lg:text-[60px] lg:leading-[72px] lg:tracking-[-1.2px]">
              {titleHighlight ? (
                <>
                  {titleLead}:{" "}
                  <span className="lg:inline-flex lg:items-center lg:bg-status-error-bg lg:px-2">
                    {titleHighlight}
                  </span>
                </>
              ) : (
                titleLead
              )}
            </h1>
            <p className="max-w-[319px] text-sm leading-5 text-text-muted sm:max-w-[420px] sm:text-base sm:leading-7 lg:max-w-[592px] lg:text-xl lg:leading-[30px]">
              {hero.description}
            </p>
          </div>

          <div className="hidden flex-col gap-3 lg:flex">
            <p className="text-base font-medium leading-6 text-text-muted">
              {hero.highlights}
            </p>
            <div className="flex flex-wrap items-center gap-2 bg-surface-inverse px-4 py-2 text-card dark:bg-surface-secondary dark:text-text-heading">
              <span className="text-base font-semibold leading-7 sm:text-lg lg:text-xl lg:leading-[30px]">
                {hero.stats.count}
              </span>
              <div className="flex items-center gap-1.5">
                <span className="flex size-5 items-center justify-center rounded-full bg-card dark:bg-surface-primary">
                  <Star
                    className="size-3 text-card-foreground dark:text-text-heading"
                    fill="currentColor"
                    stroke="none"
                  />
                </span>
                <span className="text-base font-semibold leading-7 sm:text-lg lg:text-xl lg:leading-[30px]">
                  {hero.stats.rating}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full justify-center lg:w-auto lg:justify-end">
          {isDesktop !== true ? (
            <CVUploadFormMobile
              onUpload={handleMobileUpload}
              isUploading={uploadingForm === "mobile"}
              isSignedIn={isAuthenticated}
              className="lg:hidden"
            />
          ) : null}
          {isDesktop === true ? (
            <CVUploadForm
              onUpload={handleDesktopUpload}
              isUploading={uploadingForm === "desktop"}
              isSignedIn={isAuthenticated}
              className="hidden lg:block lg:mx-0"
            />
          ) : null}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="hidden lg:flex justify-center pb-6">
        <button
          onClick={() => {
            const nextSection = document.querySelector(
              "section:nth-of-type(2)",
            );
            nextSection?.scrollIntoView({ behavior: "smooth" });
          }}
          className="flex flex-col items-center gap-2 text-text-muted hover:text-text-heading transition-colors cursor-pointer"
        >
          <span className="text-sm font-medium">Scroll to explore</span>
          <ChevronDown className="size-5" />
        </button>
      </div>
    </section>
  );
}
